import { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Paper, Snackbar, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { projectService } from '@/services/projectService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import SectionHeader from '@/components/researcher/SectionHeader'
import StepLayout from '@/components/researcher/wizard/StepLayout'
import FormField from '@/components/researcher/wizard/FormField'
import WizardTextField from '@/components/researcher/wizard/WizardTextField'
import TipsCard from '@/components/researcher/wizard/TipsCard'
import QuestionListEditor from '@/components/questions/QuestionListEditor'
import type { EditableQuestion, QuestionKind } from '@/components/questions/types'
import { apiQuestionToEditable, editableToApiQuestion, type ApiQuestion } from '@/components/questions/apiAdapter'

/** Same question kinds as the create-study wizard. */
const TEMPLATE_KINDS: QuestionKind[] = ['text', 'single', 'multi', 'scale', 'nps', 'rank']

interface TemplateMeta {
  id: number
  name: string
  instructions: string
  interpretation: string
}

function Card({ children }: { children: React.ReactNode }) {
  const theme = useTheme()
  return (
    <Paper
      variant="outlined"
      sx={{ p: 3, borderRadius: `${theme.affexy.radii.lg / 1.33}px`, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2.25 }}
    >
      {children}
    </Paper>
  )
}

export default function TemplateEditor() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questionnaires')
  const { t: tCommon } = useTranslation('common')
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [meta, setMeta] = useState<TemplateMeta>({ id: 0, name: '', instructions: '', interpretation: '' })
  const [questions, setQuestions] = useState<EditableQuestion[]>([])
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isEdit) return
    let cancelled = false
    setLoading(true)
    projectService
      .getQuestionnaireTemplate(Number(id))
      .then((data) => {
        if (cancelled) return
        setMeta({ id: data.id, name: data.name, instructions: data.instructions ?? '', interpretation: data.interpretation ?? '' })
        setQuestions(((data.questions ?? []) as unknown as ApiQuestion[]).map(apiQuestionToEditable))
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : t('loadTemplateFailed')))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [id, isEdit, t])

  const canSave = !!meta.name.trim() && questions.length > 0 && !saving

  const handleSubmit = async () => {
    setSaving(true)
    setError(null)
    const payload = {
      id: meta.id,
      name: meta.name,
      instructions: meta.instructions,
      interpretation: meta.interpretation,
      questions: questions.map(editableToApiQuestion),
    }
    try {
      const saved = isEdit
        ? await projectService.updateQuestionnaireTemplate(Number(id), payload as never)
        : await projectService.createQuestionnaireTemplate(payload as never)
      // Back to the library with the saved template open in the preview.
      navigate(`/questionnaires?t=${saved.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('saveFailed'))
      setSaving(false)
    }
  }

  const tips = t('tips', { returnObjects: true }) as string[]

  const footer = (
    <Box
      component="footer"
      sx={{
        px: { xs: 2.5, md: 5 },
        py: 1.75,
        bgcolor: '#fff',
        borderTop: `1px solid ${c.ink08}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Button color="inherit" startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} onClick={() => navigate('/questionnaires')} sx={{ color: c.ink72 }}>
        {tCommon('cancel')}
      </Button>
      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
        disabled={!canSave}
        onClick={handleSubmit}
      >
        {saving ? t('saving') : t('saveTemplate')}
      </Button>
    </Box>
  )

  return (
    <ResearcherShell
      active="templates"
      crumbs={[t('title'), isEdit ? t('editTemplate') : t('createTemplate')]}
      hideTopBarAction
      footer={loading ? undefined : footer}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <StepLayout
          title={isEdit ? t('editTemplate') : t('createTemplate')}
          subtitle={t('subtitle')}
          aside={
            <>
              <TipsCard title={t('tipsTitle')} tips={tips} />
              <Paper variant="outlined" sx={{ p: 2.25, borderRadius: `${theme.affexy.radii.md}px`, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: c.gray50, display: 'grid', placeItems: 'center' }}>
                  <HelpOutlineIcon sx={{ fontSize: 18, color: c.coral }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t('questionsSummary', { count: questions.length })}</Typography>
              </Paper>
            </>
          }
        >
          {/* Template details */}
          <Card>
            <SectionHeader title={t('templateDetails')} />
            <FormField label={t('templateName')} required>
              <WizardTextField value={meta.name} onChange={(e) => setMeta((p) => ({ ...p, name: e.target.value }))} />
            </FormField>
          </Card>

          {/* Questions — shared reusable builder, same kinds as the study wizard */}
          <Card>
            <SectionHeader title={t('questions')} />
            <QuestionListEditor
              questions={questions}
              onChange={setQuestions}
              config={{ allowedKinds: TEMPLATE_KINDS }}
            />
          </Card>
        </StepLayout>
      )}

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </ResearcherShell>
  )
}
